import { Home, MapPin, Video } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../../../lib/utils';

/** Stored location kind under the slot-level-locations model. */
export type ActivityLocationKind = 'venue' | 'at_home' | 'online';

/** One bookable location of an activity (the activity's location SET). */
export interface ActivityLocationListItem {
  id: string;
  kind: ActivityLocationKind;
  /** Display label (e.g. "Studio Bastille"); falls back to the address. */
  label?: string;
  /** Street address for a venue / at_home base. */
  address?: string;
  /** Makes the label a link (e.g. a venue at a Hub links to the Hub's page). */
  href?: string;
  isAtHome?: boolean;
  /**
   * Whether this row links to a map element (so it gets the hover affordance and
   * reports hover). Default true; set false for rows with no map counterpart (e.g.
   * an online row). Only matters when `onItemHover` is provided.
   */
  hoverable?: boolean;
}

export interface ActivityLocationListProps {
  /** The distinct bookable locations to list. */
  locations: ActivityLocationListItem[];
  /**
   * Optional heading shown above the list (e.g. "Available at 3 locations").
   * Omit to render just the rows.
   */
  heading?: ReactNode;
  /** Hovering a row reports its id (e.g. so a map can highlight the matching pin/zone). */
  onItemHover?: (id: string) => void;
  /** Pointer left the hovered row. */
  onItemLeave?: () => void;
  className?: string;
}

const iconForKind = (item: ActivityLocationListItem) => {
  if (item.kind === 'online') return Video;
  if (item.kind === 'at_home' || item.isAtHome) return Home;
  return MapPin;
};

/**
 * Renders an activity's location SET (Section 4 C1/C2 of the slot-level-locations
 * spec) as a compact, icon-prefixed list. One activity is one list row elsewhere
 * (F10); this component is the per-activity DETAIL that enumerates the distinct
 * places its slots run at. Purely presentational: the caller supplies already
 * de-duplicated, localized items.
 *
 * Online rows are signalled by the Video icon only, in the same neutral palette
 * as venue/at-home (no off-brand accent), matching the slot location picker.
 * Labelled venues keep their street address as a muted second line so the address
 * is never dropped.
 */
export function ActivityLocationList({
  locations,
  heading,
  onItemHover,
  onItemLeave,
  className
}: ActivityLocationListProps) {
  if (locations.length === 0) return null;

  const interactive = Boolean(onItemHover);

  return (
    <div className={className}>
      {heading ? <p className="px-3 pt-2 text-sm font-medium text-neutral-700">{heading}</p> : null}
      <ul className="px-3 py-1 text-neutral-600">
        {locations.map((loc) => {
          const Icon = iconForKind(loc);
          const isOnline = loc.kind === 'online';
          // Online has no address; when a venue has both a label and an address, show
          // the label as the primary line and the address as a muted second line.
          const primary = loc.label || loc.address || '';
          const secondary = loc.label && loc.address && !isOnline ? loc.address : undefined;
          const rowInteractive = interactive && loc.hoverable !== false;
          return (
            <li
              key={loc.id}
              className={cn(
                'flex items-start gap-1.5 py-1 text-sm',
                rowInteractive && '-mx-2 cursor-default rounded-md px-2 transition-colors hover:bg-neutral-100/70'
              )}
              onMouseEnter={rowInteractive ? () => onItemHover?.(loc.id) : undefined}
              onMouseLeave={rowInteractive ? () => onItemLeave?.() : undefined}
            >
              <Icon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-neutral-500" aria-hidden />
              <span className="min-w-0">
                {loc.href ? (
                  <a
                    href={loc.href}
                    className="block truncate font-medium text-neutral-800 underline-offset-2 hover:underline"
                  >
                    {primary}
                  </a>
                ) : (
                  <span className="block truncate">{primary}</span>
                )}
                {secondary ? <span className="block truncate text-xs text-neutral-500">{secondary}</span> : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
